import {
  Task,
  TaskType,
  TaskAction,
  BotTag,
  ApprovalConfigUI,
  ValidationConfig,
  ExtractionConfig,
  DataSourceConfigUI,
  StartnewConfigUI,
  SkipConfigUI,
  CounterConfigUI,
  WritebackConfigUI,
} from '@/lib/sdk/chatbot/types';
import { Xid } from 'xid-ts';
import { renumberRows } from './valueRef';

const uuidv4 = () => crypto.randomUUID();

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GridRow {
  id: string;
  _localId: string;
  _errors?: Record<string, string>;
  _hasAdvanced: boolean;
  // Inline-editable columns
  name: string;
  description: string;
  type: TaskType;
  data: string;
  group: string;
  // Advanced config — edited via TaskConfigPanel
  actions: TaskAction[];
  approvalConfig?: ApprovalConfigUI;
  validationConfig?: ValidationConfig;
  extractionConfig?: ExtractionConfig;
  dataSourceConfig?: DataSourceConfigUI;
  startnewConfig?: StartnewConfigUI;
  autoFillConfig?: Record<string, string>;
  skipConfig?: SkipConfigUI;
  counterConfig?: CounterConfigUI;
  writebackConfigs?: WritebackConfigUI[];
  mutable: boolean;
  maskOutput: boolean;
  requirePrefill: boolean;
  subtasks: Task[];
  softTtlValue?: number;
  softTtlUnit?: 'hours' | 'days';
  documentLinks: string[];
  tags: BotTag[];
}

export type GridAction =
  | { type: 'ADD_ROWS'; count: number }
  | { type: 'SET_CELL'; localId: string; col: 'name' | 'description' | 'type' | 'data' | 'group'; value: string }
  | { type: 'SET_ADVANCED'; localId: string; config: Partial<GridRow> }
  | { type: 'DELETE_ROW'; localId: string }
  | { type: 'DUPLICATE_ROW'; localId: string }
  | { type: 'SET_ROW_ERRORS'; localId: string; errors: Record<string, string> }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'RESET' }
  | { type: 'LOAD_ROWS'; rows: Task[] }
  | { type: 'APPEND_GRID_ROWS'; rows: GridRow[] }
  | { type: 'REORDER_ROWS'; activeId: string; overId: string };

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function blankRow(): GridRow {
  return {
    id: new Xid().toString(),
    _localId: uuidv4(),
    _hasAdvanced: false,
    name: '',
    description: '',
    type: TaskType.REGULAR,
    data: '',
    group: '',
    actions: [],
    mutable: false,
    maskOutput: false,
    requirePrefill: false,
    subtasks: [],
    documentLinks: [],
    tags: [],
  };
}

export function taskToGridRow(task: Task): GridRow {
  const nonGroupTags = task.tags?.filter((t) => t.key !== 'group') ?? [];
  const hasAdvanced =
    (task.actions?.length ?? 0) > 0 ||
    task.mutable ||
    task.maskOutput === true ||
    task.requirePrefill === true ||
    (task.documentLinks?.length ?? 0) > 0 ||
    nonGroupTags.length > 0 ||
    task.softTtlValue != null;

  return {
    id: task.id,
    _localId: uuidv4(),
    _hasAdvanced: hasAdvanced,
    name: task.name,
    description: task.description ?? '',
    type: task.type,
    data: task.data ?? '',
    group: task.tags?.find((t) => t.key === 'group')?.value?.toString() ?? '',
    actions: task.actions ?? [],
    approvalConfig: task.approvalConfig,
    validationConfig: task.validationConfig,
    extractionConfig: task.extractionConfig,
    dataSourceConfig: task.dataSourceConfig,
    startnewConfig: task.startnewConfig,
    autoFillConfig: task.autoFillConfig,
    skipConfig: task.skipConfig,
    counterConfig: task.counterConfig,
    writebackConfigs: task.writebackConfigs,
    mutable: task.mutable,
    maskOutput: task.maskOutput ?? false,
    requirePrefill: task.requirePrefill ?? false,
    subtasks: task.subtasks ?? [],
    softTtlValue: task.softTtlValue,
    softTtlUnit: task.softTtlUnit,
    documentLinks: task.documentLinks ?? [],
    tags: nonGroupTags,
  };
}

export function gridRowToTask(row: GridRow): Task {
  return {
    id: row.id,
    name: row.name,
    description: row.description || undefined,
    type: row.type,
    data: row.data || undefined,
    actions: row.actions.length > 0 ? row.actions : undefined,
    approvalConfig: row.approvalConfig,
    validationConfig: row.validationConfig,
    extractionConfig: row.extractionConfig,
    dataSourceConfig: row.dataSourceConfig,
    startnewConfig: row.startnewConfig,
    autoFillConfig: row.autoFillConfig,
    skipConfig: row.skipConfig,
    counterConfig: row.counterConfig,
    writebackConfigs: row.writebackConfigs,
    mutable: row.mutable,
    maskOutput: row.maskOutput || undefined,
    requirePrefill: row.requirePrefill || undefined,
    subtasks: row.subtasks.length > 0 ? row.subtasks : undefined,
    softTtlValue: row.softTtlValue,
    softTtlUnit: row.softTtlUnit,
    documentLinks: row.documentLinks.length > 0 ? row.documentLinks : undefined,
    tags: (() => {
      const nonGroupTags = row.tags.filter((t) => t.key !== 'group');
      const groupTag = row.group.trim() ? [{ key: 'group', value: row.group.trim() }] : [];
      const merged = [...nonGroupTags, ...groupTag];
      return merged.length > 0 ? merged : undefined;
    })(),
  };
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

export function taskGridReducer(state: GridRow[], action: GridAction): GridRow[] {
  switch (action.type) {
    case 'ADD_ROWS':
      return [...state, ...Array.from({ length: action.count }, blankRow)];

    case 'SET_CELL': {
      const next = state.map((row) => {
        if (row._localId !== action.localId) return row;
        const cleared = { ...row._errors, [action.col]: '' };
        if (action.col === 'type') {
          return { ...row, type: action.value as TaskType, data: '', _errors: cleared };
        }
        return { ...row, [action.col]: action.value, _errors: cleared };
      });
      // Naming a blank row adds a task and clearing a name removes one, shifting the rows after it.
      return action.col === 'name' ? renumberRows(state, next) : next;
    }

    case 'SET_ADVANCED': {
      const { localId, config } = action;
      return state.map((row) => {
        if (row._localId !== localId) return row;
        const updated = { ...row, ...config };
        updated._hasAdvanced =
          (updated.actions?.length ?? 0) > 0 ||
          updated.mutable ||
          updated.maskOutput === true ||
          updated.requirePrefill === true ||
          (updated.documentLinks?.length ?? 0) > 0 ||
          (updated.tags?.length ?? 0) > 0 ||
          updated.softTtlValue != null;
        return updated;
      });
    }

    case 'DELETE_ROW':
      return renumberRows(state, state.filter((row) => row._localId !== action.localId));

    case 'DUPLICATE_ROW': {
      const idx = state.findIndex((row) => row._localId === action.localId);
      if (idx === -1) return state;
      const src = state[idx];
      const copy: GridRow = {
        ...src,
        id: new Xid().toString(),
        _localId: uuidv4(),
        _errors: undefined,
        group: src.group,
        actions: [...src.actions],
        documentLinks: [...src.documentLinks],
        tags: src.tags.map((t) => ({ ...t })),
        subtasks: [...src.subtasks],
      };
      return renumberRows(state, [...state.slice(0, idx + 1), copy, ...state.slice(idx + 1)]);
    }

    case 'SET_ROW_ERRORS':
      return state.map((row) =>
        row._localId === action.localId ? { ...row, _errors: action.errors } : row
      );

    case 'CLEAR_ERRORS':
      return state.map((row) => ({ ...row, _errors: {} }));

    case 'RESET':
      return [blankRow(), blankRow(), blankRow()];

    case 'LOAD_ROWS':
      return action.rows.map(taskToGridRow);

    case 'APPEND_GRID_ROWS': {
      const nonEmpty = state.filter(
        (r) => r.name.trim() || r.description.trim() || r.data.trim() || r.group.trim() || r._hasAdvanced
      );
      return [...nonEmpty, ...action.rows];
    }

    case 'REORDER_ROWS': {
      const fromIdx = state.findIndex((r) => r._localId === action.activeId);
      const toIdx = state.findIndex((r) => r._localId === action.overId);
      if (fromIdx === -1 || toIdx === -1) return state;
      const next = [...state];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return renumberRows(state, next);
    }

    default:
      return state;
  }
}
